import React, { useState, useEffect, CSSProperties } from 'react';
import { Modal } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import ModalEditBody from './ModalEditBody';
import ModalEditBodyDelete from './ModalEditBodyDelete';
import ModalEditFooter from '../../common/ModalEditFooter';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, createNews, deleteNews, updateNews } from '../../../redux/slices/news';
import moment from 'moment';
import { EDIT_ACTION } from '../../../constants';
import type { CreateNewsParams, DeleteNewsParams, NewsType, UpdateNewsParamsClient } from '../../../types/news';

const ModalEditNews = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const Today = new Date();
  const { dialogMode, isDialogOpen, news, newssForManage } = useSelector((state: RootState) => state.news);
  const [order, setOrder] = useState<number>(0);
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [publicationStartAt, setPublicationStartAt] = useState<Date | null>(null);
  const [publicationEndAt, setPublicationEndAt] = useState<Date | null>(null);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [buttonLabel, setButtonLabel] = useState<string>('');
  const [deleteFlag, setDeleteFlag] = useState<boolean>(false);
  const [isUniqueOrder, setIsUniqueOrder] = useState<boolean>(false);
  const isPositiveOrder: boolean = order > 0;
  const strPublicationStartAt: string = publicationStartAt ? publicationStartAt.toDateString() : null;
  const strPublicationEndAt: string = publicationEndAt ? publicationEndAt.toDateString() : null;
  const requiredStartDatePickerStyle: CSSProperties = isDraft === false && publicationStartAt === null ? { border: '1px solid red' } : {};
  const requiredEndDatePickerStyle: CSSProperties = isDraft === false && publicationEndAt === null ? { border: '1px solid red' } : {};

  // SIDE EFFECT
  // ================================================================================

  /**
   * 確認用
   */
  // useEffect(() => {
  // }, []);

  /**
   * 新規作成モード以外では初期値を入れておく
   */
  useEffect(() => {
    if (dialogMode !== EDIT_ACTION.CREATE && news) {
      setOrder(news.order);
      setIsDraft(news.isDraft);
      setTitle(news.title);
      setMessage(news.message);
      setIsUniqueOrder(true);
      setPublicationStartAt(news.publicationStartAt !== null ? moment(news.publicationStartAt.toString()).toDate() : null);
      setPublicationEndAt(news.publicationEndAt !== null ? moment(news.publicationEndAt.toString()).toDate() : null);
    }
  }, [news]);

  /**
   * モードに併せてタイトルとボタン名を設定
   */
  useEffect(() => {
    if (dialogMode === EDIT_ACTION.CREATE) {
      setDialogTitle(t('information.news.dialog.titleCreate'));
      setButtonLabel(t('dialog.create'));
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      setDialogTitle(t('information.news.dialog.titleUpdate'));
      setButtonLabel(t('dialog.update'));
    } else if (dialogMode === EDIT_ACTION.DELETE) {
      setDialogTitle(t('information.news.dialog.titleDelete'));
      setButtonLabel(t('dialog.delete'));
    } else {
      setDialogTitle('');
      setButtonLabel('');
    }
  }, [dialogMode]);

  /**
   * orderの重複チェック
   */
  useEffect(() => {
    // 新規作成モードでは同じorderのものがないかチェック
    if (dialogMode === EDIT_ACTION.CREATE) {
      const displayNewss: NewsType[] | undefined = newssForManage?.filter((aNews) => aNews.isDraft === false);
      const sameOrder: NewsType | undefined = displayNewss?.find((aNews) => aNews.order === Number(order));
      if (sameOrder !== undefined) {
        setIsUniqueOrder(false);
      } else {
        setIsUniqueOrder(true);
      }
      // 更新モードでは自分以外のnewssに同じ名前のものがないかチェック
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      const displayNewss: NewsType[] | undefined = newssForManage?.filter((aNews) => aNews.isDraft === false);
      const sameOrder: NewsType | undefined = displayNewss?.find((aNews) => {
        if (aNews.id !== news.id && aNews.order === order) {
          return true;
        } else {
          return false;
        }
      });
      if (sameOrder !== undefined) {
        setIsUniqueOrder(false);
      } else {
        setIsUniqueOrder(true);
      }
      // 削除モードでは何もしない
    } else if (dialogMode === EDIT_ACTION.DELETE) {
      setIsUniqueOrder(false);
    } else {
      setIsUniqueOrder(false);
    }
  }, [order]);

  // EVENT HANDLER
  // ================================================================================

  const resetModal = (): void => {
    dispatch(closeDialog());
    setOrder(0);
    setIsDraft(true);
    setTitle('');
    setMessage('');
    setPublicationStartAt(null);
    setPublicationEndAt(null);
    setDeleteFlag(false);
  };

  /**
   * orderを変更した時
   */
  const handleOrderChange = (event: React.ChangeEvent<{ value: number }>): void => {
    const newOrder: number = event.target.value;
    if (newOrder >= 0) {
      setOrder(Number(newOrder));
    } else {
      // do nothing
    }
  };

  /**
   * タイトルを変更した時
   */
  const handleTitleChange = (event: React.ChangeEvent<{ value: string }>): void => {
    setTitle(event.target.value);
  };

  /**
   * 本文を変更した時
   */
  const handleMessageChange = (event: React.ChangeEvent<{ value: string }>): void => {
    setMessage(event.target.value);
  };

  /**
   * 日付を変更した時
   */
  const handlePublicationStartAtChange = (selectedDate: Date): void => {
    // console.log(selectedDate);
    setPublicationStartAt(selectedDate);
  };

  const handlePublicationEndAtChange = (selectedDate: Date): void => {
    // console.log(selectedDate);
    setPublicationEndAt(selectedDate);
  };

  /**
   * 下書きフラグを変更した時
   */
  const handleToggleDraft = (): void => {
    if (isDraft === true) {
      setOrder(order);
    }
    setIsDraft(!isDraft);
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleSave = (): void => {
    // console.log(strPublicationStartAt);
    // 新規作成時
    if (dialogMode === EDIT_ACTION.CREATE) {
      // setTradingCompanyCode(tradingCompanyCode); user情報から入手
      const params: CreateNewsParams = {
        order,
        isDraft,
        title,
        message,
        publicationStartAt: strPublicationStartAt,
        publicationEndAt: strPublicationEndAt,
      };
      dispatch(createNews(params));

      // 更新時
    } else if (dialogMode === EDIT_ACTION.UPDATE && news) {
      const params: UpdateNewsParamsClient = {
        id: news.id,
        order,
        isDraft,
        title,
        message,
        publicationStartAt: strPublicationStartAt,
        publicationEndAt: strPublicationEndAt,
      };
      dispatch(updateNews(params));

      // 削除時
    } else if (dialogMode === EDIT_ACTION.DELETE && news) {
      const params: DeleteNewsParams = {
        id: news.id,
      };
      dispatch(deleteNews(params));

      // その他
    } else {
      // do nothing
    }
    resetModal();
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    setDeleteFlag(!deleteFlag);
  };

  // VIEW
  // ================================================================================

  return (
    <Modal id="modalEditNews" className="modal-dialog-scrollable" open={isDialogOpen} maxWidth={'xs'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'newspaper'} title={dialogTitle} />

      {/* テキスト入力：新規作成・更新時 */}
      {(dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.UPDATE) && (
        <ModalEditBody
          labelOrder={t('information.order')}
          order={order}
          invalidOrder={!isDraft && (!isPositiveOrder || !isUniqueOrder)}
          onChangeOrder={handleOrderChange}
          labelTitle={t('information.title')}
          title={title}
          invalidTitle={!title}
          onChangeTitle={handleTitleChange}
          labelMessage={t('information.message')}
          message={message}
          invalidMessage={!message}
          onChangeMessage={handleMessageChange}
          labelStartDate={t('information.news.publicationStart')}
          selectedStartDate={publicationStartAt}
          styleStartDate={requiredStartDatePickerStyle}
          minStartDate={Today}
          maxStartDate={publicationEndAt ? publicationEndAt : null}
          onChangeStartDate={handlePublicationStartAtChange}
          labelEndDate={t('information.news.publicationEnd')}
          selectedEndDate={publicationEndAt}
          styleEndDate={requiredEndDatePickerStyle}
          minEndDate={publicationStartAt ? publicationStartAt : Today}
          onChangeEndDate={handlePublicationEndAtChange}
          labelDraft={t('information.draft')}
          checked={isDraft}
          onToggleDraft={handleToggleDraft}
        />
      )}

      {/* 削除時 */}
      {dialogMode === 'DELETE' && <ModalEditBodyDelete description={t('information.news.dialog.card1')} title={title} message={message} />}

      <ModalEditFooter
        mode={dialogMode}
        theme={dialogMode === EDIT_ACTION.DELETE ? 'danger' : 'primary'}
        disabled={
          (dialogMode === EDIT_ACTION.CREATE && (!title || !message || (!isDraft && (!isUniqueOrder || !isPositiveOrder || !publicationStartAt || !publicationEndAt)))) ||
          (dialogMode === EDIT_ACTION.UPDATE && (!title || !message || (!isDraft && (!isUniqueOrder || !isPositiveOrder || !publicationStartAt || !publicationEndAt)))) ||
          (dialogMode === EDIT_ACTION.DELETE && !deleteFlag)
        }
        labelCancel={t('dialog.cancel')}
        labelSave={buttonLabel}
        labelCheckBox={t('dialog.validDelete')}
        deleteFlag={deleteFlag}
        onCancel={handleCancel}
        onSave={handleSave}
        onToggle={handleToggleDelete}
      />
    </Modal>
  );
};

export default ModalEditNews;
